import * as Common from '@tfo/common'
import { InvoiceConfiguration } from '@tfo/common/src/interfaces'
import { NationalIdOrBirthDateType } from '@tfo/common/src/models'
import { Network } from '@tfo/common/src/services'
import { action, observable } from 'mobx'
import $http from '../utils/$http'
import { ReportStore } from './ReportStore'
import { ReportsStore } from './ReportsStore'

/** Minimum type Requirments from ClientViewStore class code*/
interface RootStore {
	userStore: {
		user: {
			companyId: number
			id: number
			language: string
			roles: Array<{ role: string }>
		}
	}
	network: Common.services.Network
}
export class ClientViewStore<RootStoreType extends RootStore = any> {
	@observable rootStore: RootStore
	@observable selectedReportPackage: Common.models.ReportPackage
	@observable externalAtsReference: string
	@observable modalOpen: boolean
	@observable userModalOpen: boolean
	@observable activeReportPackages: Common.models.ReportPackage[] = []


	@observable hideBackButton: boolean = false
	@observable showGroupOrderWithGroupId: string

	@observable network: Network

	@observable reportStore: ReportStore
	@observable reportsStore: ReportsStore
	@observable company: Common.models.ICompany
	@observable flatSubsidiaries: Common.models.ICompany[]
	@observable loading: boolean = false
	@observable showHeaderMenu: boolean = false
	@observable invalidNationalId: boolean = false
	@observable invalidEmail: boolean = false
	@observable createServerError: boolean = false
	@observable isExistingIndividual: boolean = false
	@observable duplicateOrder: boolean = false

	@observable invoiceReferencePersonRule?: Common.interfaces.InvoiceRule
	@observable companySpecificInvoicingRules: Array<Common.interfaces.InvoiceRule> = []
	@observable invoiceConfiguration: Common.interfaces.InvoiceConfiguration


	@observable externalHostedOrderFlow: boolean = false
	@observable lockedMode: boolean = false

	@observable companyHasUsersToShareReportWith: boolean = true

	constructor(rootStore: RootStoreType) {
		this.rootStore = rootStore
		this.network = rootStore.network
		this.reportStore = new ReportStore(this.rootStore)
		this.externalHostedOrderFlow = JSON.parse(window.sessionStorage.getItem('externalHostedOrderFlow') || 'false')
	}

	@action getOrderIdByDeliveryId = (deliveryId: string) => {
		return $http.get<any>(`/order/report/${deliveryId}/orderid`)
	}

	@action checkIfIndividualExists = async (
		individualEmail: string, 
		ordererCompanyId: number,
		individualNationalId: NationalIdOrBirthDateType | null) => {
		try {
			const response = await $http.post<{ exists: boolean }>("/order/individual/concurrent", {
				individualEmail: individualEmail,
				individualNationalId: individualNationalId,
				ordererCompanyId: ordererCompanyId
			})
			this.isExistingIndividual = response.exists
		}
		catch (error) {
			console.error(error)
		}
	}
	
	@action init = async () => {
		this.loading = true
		const companyId = this.rootStore.userStore.user?.companyId
		if (companyId) {

			await this.loadCompanyDataById(companyId, true)
			if (this.activeReportPackages.length <= 0) {
				this.getActiveReportPackages(companyId)
			}

		}

		this.loading = false
	}
	
	async getReportPackageAddons(reportPackageId: number): Promise<[]> {
		return await $http.get(`/PartnerAddons/${reportPackageId}/partner`)
	}

	async getActiveReportPackages(companyId: number): Promise<void> {
		const concernStructure = await $http.get<Array<Common.models.ICompany>>(`/company/flat/${companyId}?subsidiaries=true`)
		concernStructure?.map(async company => {
			const activeReportPackages = await $http.get<Array<Common.models.ReportPackage>>(`/order/service-packages/active/${company.id}`)
			if (activeReportPackages) {
				this.activeReportPackages.push(...activeReportPackages)
			}
		})
	}

	async loadCompanyDataById(companyId: number, subsidiaries: boolean): Promise<void> {
		const company = await $http.get<Common.models.ICompany>(`/company/${companyId}?subsidiaries=${subsidiaries}`)

		this.company = company

		if (this.company?.subsidiaries) {
			const companySubsidiaries = await $http.get<Array<Common.models.ICompany>>(`/company/flat/${companyId}?subsidiaries=${subsidiaries}`)
			this.flatSubsidiaries = companySubsidiaries.sort(({ name: a }, { name: b }) => a < b ? -1 : a > b ? 1 : 0)
		}
	}

	@action getCompanyInvoicingRules = async (companyId: number): Promise<void> => {
		await $http.get<any>(`/invoicing/configuration/${companyId}`).then(
			response => {
				const invoiceConfiguration: InvoiceConfiguration = response.invoiceConfiguration

				this.invoiceConfiguration = invoiceConfiguration
				const filteredResponse: Array<Common.interfaces.InvoiceRule> = invoiceConfiguration.invoiceRules.filter(rule => !rule.key.startsWith("special."))
				if (invoiceConfiguration.invoiceRules.find(rule => rule.key === "special.reference.person") !== undefined) {
					this.invoiceReferencePersonRule = invoiceConfiguration.invoiceRules.find(rule => rule.key === "special.reference.person")
				}
				else {
					this.invoiceReferencePersonRule = null
				}
				this.companySpecificInvoicingRules = filteredResponse !== undefined && filteredResponse !== null ? [...filteredResponse] : []
			}
		)
	}

	@action async getCheckpointsByType(type: Common.enums.ReportPackageType): Promise<Array<Common.models.CheckpointSettings>> {
		const response = await $http.get<Array<Common.models.CheckpointSettings>>(`/order/checkpoints/${type}`)
		return response;
	}


	@action async getReportPackageBySubsidiaries(subsidiariesId: number) {
		this.activeReportPackages = []
		this.activeReportPackages = await $http.get<Array<Common.models.ReportPackage>>(`/order/service-packages/active/${subsidiariesId}`)
	}

	@action onShowHeaderMenu() {
		this.showHeaderMenu = !this.showHeaderMenu
	}

	@action activateLockedMode(value : boolean) {
		this.lockedMode = value
	}

	@action async getSubsidiariesCompany(subsidiariesId: number) {
		await this.getCompanyInvoicingRules(subsidiariesId)
		return await $http.get<Common.models.ICompany>(`/company/${subsidiariesId}?subsidiaries=false`)
	}

	@action async getCustomer() {
		return await $http.get<Common.models.ICustomer>(`/user/customer/${this.rootStore.userStore.user?.id}`)
	}

	@action async getRecipientsId(userId: number) {
		const { recipientIds } = await $http.get<any>(`/user/customer/${userId}`)
		return recipientIds
	}

	@action async setReportStore() {
		this.loading = true
		this.reportsStore = new ReportsStore(
			this.network,
			this.rootStore.userStore.user.companyId,
			this.rootStore.userStore.user.id
		)

		await this.reportsStore.init()
		this.loading = false
	}

	@action hideModal() {
		this.modalOpen = false
		this.init()
	}

	@action toggleModal() {
		this.modalOpen = !this.modalOpen
	}

	@action toggleExternalHostedOrderFlow(value: boolean) {
		this.externalHostedOrderFlow = value
		window.sessionStorage.setItem('externalHostedOrderFlow', JSON.stringify(this.externalHostedOrderFlow))
	}

	@action async createNewOrder(data: Common.models.NewOrderData, companyId: number, invoiceReferencePerson: string, orderSpecificInvoiceRules: Array<Common.interfaces.OrderInvoiceRule>, files: File[]): Promise<void> {
		this.invalidNationalId = false
		this.invalidEmail = false
		this.duplicateOrder = false
		this.createServerError = false
		this.loading = true

		const fileIds = []
		for (let i = 0; i < files.length; i++) {
			const formDataFiles = new FormData()
			formDataFiles.append("file", files[i])
			const id = await $http.post<any>(`/order/filestorage?filePurposeType=CV&companyId=${companyId}&userId=${this.rootStore.userStore.user.id}`, formDataFiles)
			fileIds.push(id)
		}

		data.fileIDs = fileIds

		const order = this.buildCreateOrder(data, companyId, invoiceReferencePerson, orderSpecificInvoiceRules)
		this.resetNewOrder()

		await $http.post(`/order/place`, order)
			.catch(error => {
				switch (error.response.status) {
					case 400: this.invalidEmail = true; break;
					case 409: this.duplicateOrder = true; break;
					default: this.createServerError = true;
				}
				this.loading = false
				return error
			})
		this.loading = false
	}

	@action resetNewOrder() {
		this.invalidNationalId = false
		this.invalidEmail = false
		this.createServerError = false
		this.isExistingIndividual = false
	}

	@action buildCreateOrder(data: Common.models.NewOrderData, companyId: number, invoiceReferencePerson: string, invoiceSettings: Array<Common.interfaces.OrderInvoiceRule>): Common.models.IOrderCreate {
		const stringDate = data.birthDate?.value
		const birthDate = new Date(stringDate)

		let nationalId = { country: 'Empty', value: '' }
		if (this.selectedReportPackage.requirePersonalIdentification) {
			nationalId = data.nationalId?.value
				? { country: 'SE', value: data.nationalId.value.replace(/\D/, '') }
				: { country: 'Other', value: birthDate.toISOString().split('T')[0] } //pick the date part according to ISO 8601 (yyyy-MM-dd with .net notation)
		}

		const individual = {
			nationalId,
			firstName: data.firstName.value as string,
			lastName: data.lastName.value as string,
			email: data.email.value as string,
			phoneNumber: data.phoneNumber.value || null
		}

		const educationOrder = data.educationinstitute
			? [
				{
					institute: data.educationinstitute?.value as string,
					course: data.educationdegree?.value as string,
					from: data.educationfromdate?.value as string,
					to: data.educationtodate?.value as string
				}
			]
			: null

		const optionalCheckpoints = data.optionalCheckpoints.map(
			optionalCheckpoint => {
				return {
					checkpointId: optionalCheckpoint.checkpointId,
					isIncluded: optionalCheckpoint.isIncluded
				}
			}
		)

		const internationalBackgroundCheckCountriesList = data.countriesBackgroundCheck
			? this.ensureCountrySelection(data.countriesBackgroundCheck)
			: []
		const criminalRecordsList = data.criminalRecords
			? this.ensureCountrySelection(data.criminalRecords)
			: []
	
		const order = new Common.models.OrderCreate({
			individual: individual,
			companyId: companyId,
			invoiceAddressId: data.address.items[data.address.selected].addressId,
			servicePackageId: this.selectedReportPackage.id,
			reportPackageId: this.selectedReportPackage.id,
			note: data.comments.value,
			customerInternalComment: data.customerInternalComment.value,
			invoiceReference: data.invoiceReference.value,
			fileIDs: data.fileIDs,
			additionalOrderRecipientUserId: data.additionalOrderRecipientUserId.value,
			checkpointOptions: optionalCheckpoints,
			educationOrderRequests: educationOrder,
			countriesBackgroundCheck: internationalBackgroundCheckCountriesList,
			criminalRecords: criminalRecordsList,
			referencePerson: this.invoiceReferencePersonRule === null ? null : invoiceReferencePerson,
			invoiceSettings: invoiceSettings,
			externalAtsReference: this.externalAtsReference
		})

		return order
	}

	@action ensureCountrySelection(countryList: {
		id: string
		region: string
		country: string
		isCommonCountry: boolean
	}[]) {
		return countryList[0]?.id === '' ? [] : countryList
	}

	@action async onClickReport(order: Common.models.IOrderClient) {
		await this.reportStore.updateData(order);
	}

	@action async onClickGroupReport(grouporder: Common.models.IGroupOrder) {
		this.showGroupOrderWithGroupId = grouporder.id
	}
}
